import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquarePlus, X, Send, Star, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FeedbackWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categories = [
    { id: 'design', label: 'עיצוב ומראה' },
    { id: 'content', label: 'תוכן וטקסטים' },
    { id: 'navigation', label: 'ניווט ונוחות' },
    { id: 'images', label: 'תמונות' },
    { id: 'other', label: 'אחר' }
  ];

  const handleSubmit = async () => {
    if (!rating || !feedback.trim()) {
      toast.error('אנא מלאי דירוג והערות');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        rating,
        category,
        feedback,
        page_url: window.location.href,
        timestamp: new Date().toISOString()
      });
      
      setSubmitted(true);
      toast.success('תודה על המשוב! 🙏');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Feedback error:', error);
      toast.error('שגיאה בשליחה, נסי שוב');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset after animation
    setTimeout(() => {
      if (submitted) {
        setRating(0);
        setCategory('');
        setFeedback('');
        setSubmitted(false);
      }
    }, 300);
  };

  return (
    <>
      {/* Feedback Toggle Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-1/2 -translate-y-1/2 left-0 z-[9990] bg-[#C4956A] text-white py-3 px-2 rounded-l-none rounded-r-lg shadow-lg hover:bg-[#b38559] transition-all group"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        data-testid="feedback-toggle"
        aria-label="פתח טופס משוב"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <MessageSquarePlus className="w-4 h-4 rotate-90" />
          משוב על האתר
        </span>
      </button>

      {/* Feedback Modal */}
      <AnimatePresence>
        {isOpen && (
          <div 
            className="fixed inset-0 z-[10002] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              dir="rtl"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Heebo'] text-xl font-bold text-[#2C2C2C]">
                  משוב על האתר
                </h3>
                <button 
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="סגור"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {!submitted ? (
                <>
                  {/* Intro */}
                  <p className="text-[#2C2C2C]/70 text-sm mb-6">
                    נשמח לשמוע את דעתך על האתר החדש! המשוב שלך יעזור לנו לשפר ולהתאים אותו בצורה הטובה ביותר.
                  </p>

                  {/* Star Rating */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-3">
                      איך היית מדרגת את האתר?
                    </label>
                    <div className="flex gap-2 justify-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="p-1 transition-transform hover:scale-110"
                          data-testid={`rating-star-${star}`}
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${
                              star <= (hoverRating || rating)
                                ? 'fill-[#C4956A] text-[#C4956A]'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    {rating > 0 && (
                      <p className="text-center text-sm text-[#C4956A] mt-2">
                        {rating === 1 && 'צריך שיפור'}
                        {rating === 2 && 'לא רע'}
                        {rating === 3 && 'בסדר'}
                        {rating === 4 && 'טוב מאוד'}
                        {rating === 5 && 'מעולה!'}
                      </p>
                    )}
                  </div>

                  {/* Category Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-3">
                      על מה המשוב? (אופציונלי)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => setCategory(category === cat.id ? '' : cat.id)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                            category === cat.id
                              ? 'bg-[#C4956A] text-white border-[#C4956A]'
                              : 'border-gray-300 text-gray-600 hover:border-[#C4956A]'
                          }`}
                          data-testid={`category-${cat.id}`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Feedback Text */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-3">
                      ספרי לנו מה את חושבת
                    </label>
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="מה אהבת? מה אפשר לשפר? יש משהו שחסר?"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#C4956A] focus:ring-2 focus:ring-[#C4956A]/20 outline-none resize-none h-32 text-sm"
                      data-testid="feedback-textarea"
                    />
                  </div>

                  {/* Quick Feedback Buttons */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-3">
                      או בחרי תגובה מהירה:
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {[
                        'האתר נראה מקצועי',
                        'קל לנווט',
                        'התמונות יפות',
                        'צריך יותר מידע',
                        'הטקסטים ברורים'
                      ].map((quick) => (
                        <button
                          key={quick}
                          onClick={() => setFeedback(prev => prev ? `${prev}, ${quick}` : quick)}
                          className="px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600 hover:bg-[#C4956A]/10 hover:text-[#C4956A] transition-colors"
                        >
                          {quick}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !rating || !feedback.trim()}
                    className="w-full rounded-full py-6 bg-[#C4956A] hover:bg-[#b38559] text-white disabled:opacity-50"
                    data-testid="feedback-submit"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        שולח...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        שליחת משוב
                      </span>
                    )}
                  </Button>
                </>
              ) : (
                /* Success State */
                <div className="text-center py-8">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <ThumbsUp className="w-10 h-10 text-green-600" />
                  </div>
                  <h4 className="font-['Heebo'] text-xl font-bold text-[#2C2C2C] mb-2">
                    תודה רבה!
                  </h4>
                  <p className="text-[#2C2C2C]/70">
                    המשוב שלך התקבל ויעזור לנו לשפר את האתר
                  </p>
                  <Button
                    onClick={handleClose}
                    className="mt-6 rounded-full px-8 bg-[#C4956A] hover:bg-[#b38559] text-white"
                  >
                    סגור
                  </Button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FeedbackWidget;
